import React, { useState } from 'react';
import './ContactUs.css';
import callIcon from '../../assets/call.png';
import mailIcon from '../../assets/mail.png';
import locationIcon from '../../assets/location.png';
import { validateContactForm, validateFullName, validateEmailDetailed } from '../../utils/validation';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      // Remove numbers and special characters, keep only letters and spaces
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: lettersOnly,
      }));

      // Real-time validation for name
      if (lettersOnly.length > 0) {
        // Check if the original value contained numbers
        if (value !== lettersOnly) {
          setValidationErrors(prev => ({
            ...prev,
            name: "Name should only contain letters and spaces",
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            name: "",
          }));
        }
      } else {
        setValidationErrors(prev => ({
          ...prev,
          name: "",
        }));
      }
      return;
    }

    if (name === "email") {
      // Convert to lowercase to prevent capitalization
      const lowercaseValue = value.toLowerCase();
      setFormData(prev => ({
        ...prev,
        [name]: lowercaseValue,
      }));

      // Real-time email validation
      if (lowercaseValue.length > 0) {
        const emailValidation = validateEmailDetailed(lowercaseValue);
        if (!emailValidation.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            email: emailValidation.error,
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            email: "",
          }));
        }
      } else {
        setValidationErrors(prev => ({
          ...prev,
          email: "",
        }));
      }
      return;
    }

    // Normal case for other fields
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Enhanced validation using the same functions as Sign-in modal
    const errors = {};
    
    // Validate name using enhanced validation
    if (!formData.name) {
      errors.name = 'Name is required';
    } else {
      const nameValidation = validateFullName(formData.name);
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
      }
    }
    
    // Validate email using enhanced validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else {
      const emailValidation = validateEmailDetailed(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error;
      }
    }
    
    // Validate other fields using existing contact validation
    const contactValidation = validateContactForm(formData);
    if (!contactValidation.isValid) {
      Object.entries(contactValidation.errors).forEach(([field, message]) => {
        if (field !== 'name' && field !== 'email') {
          errors[field] = message;
        }
      });
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Clear any previous errors
    setValidationErrors({});

    try {
      // Here you would typically send the data to your backend
      // For now, we'll simulate a successful submission
      console.log('Form submitted:', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        service: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="contact-us-page">
      {/* Contact Us Section */}
      <section className="contact-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-md-6 col-sm-12 contact-form-main">
              <div className="contact-form-section">
                <h4 className="section-subtitle">Contact Form</h4>
                <h2 className="section-title">Send Us A Message</h2>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`form-control ${validationErrors.name ? 'error' : ''}`}
                      placeholder="Enter your full name"
                      autoComplete="name"
                    />
                    {validationErrors.name && (
                      <div className="error-message">{validationErrors.name}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-control ${validationErrors.email ? 'error' : ''}`}
                      placeholder="example@domain.com"
                      autoComplete="email"
                    />
                    {validationErrors.email && (
                      <div className="error-message">{validationErrors.email}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="service">Service Interest *</label>
                    <select 
                      id="service" 
                      name="service" 
                      value={formData.service}
                      onChange={handleInputChange}
                      className={`form-control ${validationErrors.service ? 'error' : ''}`}
                    >
                      <option value="">Select a service</option>
                      <option value="startup-registration">Startup Registration</option>
                      <option value="funding">Funding</option>
                      <option value="compliance">Compliance</option>
                      <option value="consultation">Consultation</option>
                    </select>
                    {validationErrors.service && (
                      <div className="error-message">{validationErrors.service}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows="5" 
                      value={formData.message}
                      onChange={handleInputChange}
                      className={`form-control ${validationErrors.message ? 'error' : ''}`}
                      placeholder="Enter your message (minimum 10 characters)"
                    ></textarea>
                    {validationErrors.message && (
                      <div className="error-message">{validationErrors.message}</div>
                    )}
                    <div className={`character-count ${
                      formData.message.length > 900 ? 'error' : 
                      formData.message.length > 800 ? 'warning' : ''
                    }`}>
                      {formData.message.length}/1000 characters
                    </div>
                  </div>
                  
                  {/* Submit Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="success-message">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="error-message">
                      Sorry, there was an error sending your message. Please try again.
                    </div>
                  )}
                  
                  <div className="form-group-submit">
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 contact-form">
              <div className="contact-info-section">
                <h4 className="section-subtitle">Contact Info</h4>
                <h2 className="section-title">Contact With Us</h2>
                <p className="contact-description">Get in touch with us. We're here to assist you.</p>
                
                <div className="contact-info-list">
                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <img src={callIcon} alt='contactIcon'/>
                    </div>
                    <div className="contact-details">
                      <span>+91 9907113949 0731 4985315</span>
                    </div>
                  </div>
                  
                  <div className="contact-info-item">
                    <div className="contact-icon">
                       <img src={mailIcon} alt='contactIcon'/>
                    </div>
                    <div className="contact-details">
                      <span>Info@compliancesarathi.in</span>
                    </div>
                  </div>
                  
                  <div className="contact-info-item">
                    <div className="contact-icon">
                       <img src={locationIcon} alt='contactIcon'/>
                    </div>
                    <div className="contact-details">
                      <span>B-101, Radhakrishna Complex, A.B Road, Indore - 452001</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactUs;
